import { format } from "date-fns";
import {
	AlertTriangle,
	AlertCircle,
	CheckSquare,
	Clock,
	TrendingUp,
	Users,
	Home,
} from "lucide-react";
import { NavigationMetricCardAlt } from "@/components/NavigationMetricCardAlt";
import type React from "react";
import { useEffect, useMemo } from "react";
import { PersonalInsightsCard } from "@/components/AIAssistant/PersonalInsightsCard";
import { ActionRequiredCard } from "@/components/dashboard/ActionRequiredCard";
import { PersonaBadgeToggle } from "@/components/dashboard/PersonaBadgeToggle";
import { SubmissionConsistencyCard } from "@/components/dashboard/SubmissionConsistencyCard";
import { ThisWeekCard } from "@/components/dashboard/ThisWeekCard";
import { TopProjectsCard } from "@/components/dashboard/TopProjectsCard";
import { FeatureFeedbackCollector } from "@/components/FeedbackCollector";
import { TimesheetStats } from "@/components/TimesheetStats";
import { PageHeader } from "@/components/blocks/page-header";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useDataSource } from "@/contexts/DataSourceContext";
import { useSettings } from "@/contexts/SettingsContext";
import { useTimesheetForScreen } from "@/contexts/TimesheetContext";
import {
	getAllViolations,
	getViolationsForEmployee,
} from "@/data/mockPolicyViolations";
import { DashboardSummaryProvider, useDashboardSummaryContext } from "@/contexts/DashboardSummaryContext";
import { aiService } from "@/services/aiService";
import type { TimesheetStatus } from "@/types/timesheet";

// Navigation state types for drilldown
type AppNavigationState = {
	timesheetDrilldown?: {
		employeeId: number;
		weekEnding: Date;
	};
	reportFilters?: {
		statuses?: TimesheetStatus[];
		employeeIds?: number[];
		dateFrom?: Date;
		dateTo?: Date;
		quickDateRange?: string;
	};
};

interface DashboardPageProps {
	onNavigate?: (path: string, state?: AppNavigationState) => void;
}

// Main Homepage Component
export const DashboardPage: React.FC<DashboardPageProps> = ({ onNavigate }) => {
	return (
		<DashboardSummaryProvider>
			<DashboardPageContent onNavigate={onNavigate} />
		</DashboardSummaryProvider>
	);
};

const DashboardPageContent: React.FC<DashboardPageProps> = ({ onNavigate }) => {
	const { currentEmployee, timesheets, projects, refreshEmployeeTimesheets } =
		useTimesheetForScreen("homepage");
	// Get pending approvals from the approvals screen to ensure consistency
	const { pendingApprovals: approvalsPending, employees } =
		useTimesheetForScreen("approvals");
	const { getScreenMode } = useDataSource();
	const { summary } = useDashboardSummaryContext();

	const mode = getScreenMode("homepage");

	// Refresh employee timesheets on mount in live mode to pick up
	// status changes (e.g., rejections) that happened since initial load
	useEffect(() => {
		if (mode === 'live') {
			refreshEmployeeTimesheets();
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mode]);

	const { organizationSettings } = useSettings();
	const overtimeEnabled = organizationSettings?.overtime?.enabled === true;

	// Generate AI insights - use backend summary data in live mode for more accurate metrics
	const personalInsights = useMemo(() => {
		const baseInsights = aiService.generatePersonalInsights(currentEmployee.id, timesheets);

		// Inject overtime insight for the employee's most recent timesheet (when overtime is enabled)
		if (overtimeEnabled) {
			const employeeTimesheets = timesheets
				.filter(ts => ts.employeeId === currentEmployee.id)
				.sort((a, b) => b.weekEnding.getTime() - a.weekEnding.getTime());
			const latestTimesheet = employeeTimesheets[0];
			if (latestTimesheet) {
				const overtimeInsight = aiService.generateOvertimeInsight(latestTimesheet);
				if (overtimeInsight) {
					baseInsights.unshift(overtimeInsight);
				}
			}
		}

		if (mode === 'live' && summary?.performanceMetrics) {
			const pm = summary.performanceMetrics;

			// Enhance workload insight with backend utilization data
			if (pm.utilization) {
				const utilizationRate = pm.utilization.rate;
				const utilizationTrend = pm.utilization.trend;
				const existingWorkloadIdx = baseInsights.findIndex(i => i.id === 'workload-trend');
				const workloadInsight = {
					id: 'workload-trend',
					type: 'workload' as const,
					title: 'Utilization Rate',
					description: `Your current utilization is ${utilizationRate}% (billable vs total hours)`,
					metric: `${utilizationTrend > 0 ? '+' : ''}${utilizationTrend}% vs last period`,
					trend: (utilizationTrend > 5 ? 'up' : utilizationTrend < -5 ? 'down' : 'stable') as 'up' | 'down' | 'stable',
					severity: (utilizationRate < 60 ? 'warning' : 'info') as 'info' | 'warning',
					actionable: utilizationRate < 60 ? 'Consider focusing on billable projects to improve utilization' : undefined,
				};
				if (existingWorkloadIdx >= 0) {
					baseInsights[existingWorkloadIdx] = workloadInsight;
				}
			}

			// Enhance submission efficiency with backend consistency data
			if (pm.submissionConsistency) {
				const sc = pm.submissionConsistency;
				const onTimeRate = Number(sc.onTimeRateLast12Weeks) * 100;
				const existingEfficiencyIdx = baseInsights.findIndex(i => i.id === 'submission-efficiency');
				const efficiencyInsight = {
					id: 'submission-efficiency',
					type: 'efficiency' as const,
					title: 'Submission Timeliness',
					description: `${onTimeRate.toFixed(0)}% of timesheets submitted on time (${sc.onTimeCount} on-time, ${sc.lateCount} late, ${sc.missingCount} missing)`,
					trend: (onTimeRate > 80 ? 'up' : onTimeRate < 50 ? 'down' : 'stable') as 'up' | 'down' | 'stable',
					severity: (onTimeRate < 50 ? 'warning' : 'info') as 'info' | 'warning',
					actionable: onTimeRate < 50 ? 'Set a reminder to submit timesheets by the deadline' : undefined,
				};
				if (existingEfficiencyIdx >= 0) {
					baseInsights[existingEfficiencyIdx] = efficiencyInsight;
				} else {
					baseInsights.push(efficiencyInsight);
				}
			}
		}

		return baseInsights;
	}, [mode, summary, currentEmployee.id, timesheets, overtimeEnabled]);

	// Helper function to get relative time
	const getRelativeTime = (date: Date): string => {
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffMins = Math.floor(diffMs / 60000);
		const diffHours = Math.floor(diffMs / 3600000);
		const diffDays = Math.floor(diffMs / 86400000);

		if (diffMins < 60)
			return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"} ago`;
		if (diffHours < 24)
			return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
		if (diffDays < 7)
			return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
		return format(date, "dd/MM/yyyy");
	};

	// Generate recent activity from timesheets or backend data
	const recentActivity = useMemo(() => {
		// Use backend data in live mode when available
		if (mode === 'live' && summary?.recentActivity?.items?.length) {
			return summary.recentActivity.items.map(item => {
				const timestamp = new Date(item.timestamp);
				let icon = Clock;
				let color = 'text-info-600';
				if (item.type === 'approved') {
					icon = CheckSquare;
					color = 'text-success-600';
				} else if (item.type === 'rejected') {
					icon = Clock;
					color = 'text-error-600';
				}
				return {
					action: item.action,
					description: item.description,
					time: getRelativeTime(timestamp),
					icon,
					color,
					timestamp,
				};
			});
		}

		// Fallback to client-side calculation for demo mode
		const activities: Array<{
			action: string;
			description: string;
			time: string;
			icon: any;
			color: string;
			timestamp: Date;
		}> = [];

		const myTimesheets = timesheets
			.filter((ts) => ts.employeeId === currentEmployee.id)
			.sort((a, b) => {
				const dateA = a.approvedAt || a.submittedAt || new Date(a.weekEnding);
				const dateB = b.approvedAt || b.submittedAt || new Date(b.weekEnding);
				return dateB.getTime() - dateA.getTime();
			});

		myTimesheets.slice(0, 5).forEach((ts) => {
			if (ts.status === "approved" && ts.approvedAt) {
				activities.push({
					action: "Timesheet Approved",
					description: `Week ending ${format(new Date(ts.weekEnding), "dd/MM/yyyy")}`,
					time: getRelativeTime(ts.approvedAt),
					icon: CheckSquare,
					color: "text-success-600",
					timestamp: ts.approvedAt,
				});
			} else if (ts.status === "submitted" && ts.submittedAt) {
				activities.push({
					action: "Timesheet Submitted",
					description: `Week ending ${format(new Date(ts.weekEnding), "dd/MM/yyyy")}`,
					time: getRelativeTime(ts.submittedAt),
					icon: Clock,
					color: "text-info-600",
					timestamp: ts.submittedAt,
				});
			} else if (ts.status === "rejected" && ts.rejectedAt) {
				activities.push({
					action: "Timesheet Rejected",
					description: `Week ending ${format(new Date(ts.weekEnding), "dd/MM/yyyy")}`,
					time: getRelativeTime(ts.rejectedAt),
					icon: Clock,
					color: "text-error-600",
					timestamp: ts.rejectedAt,
				});
			}
		});

		return activities
			.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
			.slice(0, 3);
	}, [mode, summary, currentEmployee.id, timesheets]);

	// Calculate user-specific metrics
	const userMetrics = useMemo(() => {
		// Calculate pending approvals from approvals screen data (uses managerId-based filtering)
		const userPendingApprovals = approvalsPending.length;
		const overdueApprovals = approvalsPending.filter((ts) => {
			if (!ts.submittedAt) return false;
			const daysSinceSubmission = Math.floor(
				(Date.now() - ts.submittedAt.getTime()) / (1000 * 60 * 60 * 24),
			);
			return daysSinceSubmission > 3;
		}).length;

		// Calculate rejected timesheets count
		const rejectedCount = timesheets.filter(
			(ts) => ts.employeeId === currentEmployee.id && ts.status === 'rejected'
		).length;

		// Use live data from backend if available and in live mode
		if (mode === "live" && summary?.performanceMetrics?.utilization) {
			return {
				pendingApprovals: userPendingApprovals,
				overdueApprovals,
				rejectedCount,
				utilizationRate: Math.round(
					summary.performanceMetrics.utilization.rate,
				),
				utilizationTrend: Math.round(
					summary.performanceMetrics.utilization.trend,
				),
			};
		}

		// Fall back to demo data calculation
		// Filter timesheets for current user only
		const userTimesheets = timesheets.filter(
			(ts) => ts.employeeId === currentEmployee.id,
		);

		// Calculate user's personal utilization
		const approvedTimesheets = userTimesheets.filter(
			(ts) => ts.status === "approved",
		);

		const totalHours = approvedTimesheets.reduce((sum, ts) => {
			return (
				sum +
				ts.entries.reduce((entrySum, entry) => {
					return (
						entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0)
					);
				}, 0)
			);
		}, 0);

		const billableHours = approvedTimesheets.reduce((sum, ts) => {
			return (
				sum +
				ts.entries.reduce((entrySum, entry) => {
					const project = projects.find((p) => p.id === entry.projectId);
					const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
					return entrySum + (project?.billable ? hours : 0);
				}, 0)
			);
		}, 0);

		const utilizationRate =
			totalHours > 0 ? (billableHours / totalHours) * 100 : 0;

		// Calculate utilization trend (compare with previous period)
		const now = new Date();
		const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
		const sixtyDaysAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);

		const recentTimesheets = approvedTimesheets.filter(
			(ts) => ts.weekEnding >= thirtyDaysAgo && ts.weekEnding <= now,
		);
		const previousTimesheets = approvedTimesheets.filter(
			(ts) => ts.weekEnding >= sixtyDaysAgo && ts.weekEnding < thirtyDaysAgo,
		);

		const recentTotalHours = recentTimesheets.reduce((sum, ts) => {
			return (
				sum +
				ts.entries.reduce((entrySum, entry) => {
					return (
						entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0)
					);
				}, 0)
			);
		}, 0);

		const recentBillableHours = recentTimesheets.reduce((sum, ts) => {
			return (
				sum +
				ts.entries.reduce((entrySum, entry) => {
					const project = projects.find((p) => p.id === entry.projectId);
					const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
					return entrySum + (project?.billable ? hours : 0);
				}, 0)
			);
		}, 0);

		const previousTotalHours = previousTimesheets.reduce((sum, ts) => {
			return (
				sum +
				ts.entries.reduce((entrySum, entry) => {
					return (
						entrySum + Object.values(entry.hours).reduce((a, b) => a + b, 0)
					);
				}, 0)
			);
		}, 0);

		const previousBillableHours = previousTimesheets.reduce((sum, ts) => {
			return (
				sum +
				ts.entries.reduce((entrySum, entry) => {
					const project = projects.find((p) => p.id === entry.projectId);
					const hours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
					return entrySum + (project?.billable ? hours : 0);
				}, 0)
			);
		}, 0);

		const recentUtilization =
			recentTotalHours > 0 ? (recentBillableHours / recentTotalHours) * 100 : 0;
		const previousUtilization =
			previousTotalHours > 0
				? (previousBillableHours / previousTotalHours) * 100
				: 0;
		const utilizationTrend = recentUtilization - previousUtilization;

		return {
			pendingApprovals: userPendingApprovals,
			overdueApprovals,
			rejectedCount,
			utilizationRate: Math.round(utilizationRate),
			utilizationTrend: Math.round(utilizationTrend),
		};
	}, [
		mode,
		summary,
		currentEmployee.id,
		timesheets,
		projects,
		approvalsPending,
	]);

	return (
		<div className="space-y-6">
			{/* Header with integrated persona toggle */}
			<div className="flex items-start justify-between gap-4 flex-wrap">
				<PageHeader
					title="Homepage"
	
					size={36}
					showActions={false}
					showBreadcrumb={false}
					icon={<Home />}
				/>
			<div className="flex items-center gap-4 ml-auto flex-shrink-0">
				<FeatureFeedbackCollector feature="Homepage" />
				<PersonaBadgeToggle />
			</div>
			</div>

			{<>
			{/* ========================================
          TIER 1: HERO SECTION - Most Important
          ======================================== */}
			<div className="grid gap-4 lg:grid-cols-3">
				{/* This Week Card - Primary Focus */}
				<ThisWeekCard
					onNavigateToTimesheet={() => onNavigate?.("/timesheet")}
				/>

				{/* Action Required Card - Critical Alerts */}
				<ActionRequiredCard
					onNavigateToTimesheet={(timesheetId, weekEnding) => {
						if (weekEnding) {
							// Pass weekEnding via navigation state so TimesheetGrid navigates to the correct week
							onNavigate?.("/timesheet", {
								timesheetDrilldown: {
									employeeId: currentEmployee.id,
									weekEnding: weekEnding,
								},
							});
						} else {
							onNavigate?.("/timesheet");
						}
					}}
					/>

				{/* Recent Activity Card - Moved from TIER 5 */}
				<Card>
					<CardHeader>
						<CardTitle className="text-base">Recent Activity</CardTitle>
						<CardDescription className="text-xs">
							Your latest timesheet submissions and approvals
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="space-y-3">
							{recentActivity.length > 0 ? (
								recentActivity.map((item, index) => (
									<div key={index} className="flex items-start gap-3">
										<div
											className={`p-1.5 rounded-lg bg-accent ${item.color}`}
										>
											<item.icon className="h-3.5 w-3.5" />
										</div>
										<div className="flex-1 min-w-0">
											<p className="text-sm font-medium">{item.action}</p>
											<p className="text-xs text-muted-foreground">
												{item.description}
											</p>
										</div>
										<p className="text-xs text-muted-foreground whitespace-nowrap">
											{item.time}
										</p>
									</div>
								))
							) : (
								<p className="text-sm text-muted-foreground text-center py-4">
									No recent activity to display
								</p>
							)}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* ========================================
          TIER 2: PERFORMANCE METRICS - Key Numbers
          ======================================== */}
			<div className="space-y-3">
				<div className="flex items-center gap-2">
					<div className="h-px flex-1 bg-border" />
					<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
						Performance Metrics
					</h2>
					<div className="h-px flex-1 bg-border" />
				</div>

				<TooltipProvider>
					<div data-tour="homepage-performance" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
						{/* Existing TimesheetStats component - Billable, This Month, Avg Weekly */}
						<TimesheetStats onNavigate={onNavigate} />

												{/* My Utilisation Card - Using NavigationMetricCardAlt */}
												<NavigationMetricCardAlt
													title="My Utilisation"
							value={`${userMetrics.utilizationRate}%`}
							icon={TrendingUp}
							variant={
								userMetrics.utilizationRate >= 85
									? 'success'
									: userMetrics.utilizationRate >= 75
										? 'info'
										: userMetrics.utilizationRate >= 60
											? 'warning'
											: 'error'
							}
							change={
								userMetrics.utilizationTrend !== 0
									? `${userMetrics.utilizationTrend > 0 ? "↗ +" : "↘ "}${userMetrics.utilizationTrend}%`
									: "→ No change"
							}
							changeLabel="from last month"
							onClick={() => onNavigate?.('/reports/utilisation', {
								reportFilters: {
									employeeIds: [currentEmployee.id],
								},
							})}
						/>
					</div>
				</TooltipProvider>

				{/* PHASE 2: Submission Consistency Card - Full width below metrics */}
				<SubmissionConsistencyCard />
			</div>

			{/* ========================================
          TIER 3 & 4: TIME ALLOCATION & INSIGHTS - Side by Side on Large Screens
          ======================================== */}
			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				{/* TIME ALLOCATION */}
				<div className="space-y-3">
					<div className="flex items-center gap-2">
						<div className="h-px flex-1 bg-border" />
						<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
							Time Allocation
						</h2>
						<div className="h-px flex-1 bg-border" />
					</div>

					{/* Top Projects by Time Card */}
					<TopProjectsCard
						onNavigateToProject={(projectId) => {
							onNavigate?.("/timesheet");
						}}
						maxProjects={5}
						defaultTimePeriod="last30days"
						showActiveOnly={false}
					/>
				</div>

				{/* INSIGHTS & TRENDS */}
				{personalInsights.length > 0 && (
					<div className="space-y-3">
						<div className="flex items-center gap-2">
							<div className="h-px flex-1 bg-border" />
							<h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
								Insights & Trends
							</h2>
							<div className="h-px flex-1 bg-border" />
						</div>

						{/* AI Personal Insights */}
						<PersonalInsightsCard insights={personalInsights} />
					</div>
				)}
			</div>
		</>
		}
	</div>
	);
};
